/**
 * How each platform's API fields become the eight metrics this product stores.
 *
 * Read off the five *Client.ts files, not off a document. docs/development/
 * media-field-mapping.html held the same information and had already drifted
 * from the code in two places by the time this was written — it said TikTok
 * does not populate conversion_value (it does, from total_complete_payment_rate)
 * and that LINE maps all eight metrics (its engagements is hardcoded 0).
 *
 * That is the failure /help exists to avoid, so this table is not free prose:
 * fieldMapping.test.ts greps each client for every field name listed here and
 * fails when one disappears, and fails again when a client file exists that has
 * no entry. It cannot check that a *note* is still true — only a human can —
 * but it does catch the rename and the new platform, which is how these tables
 * actually rot.
 *
 * Notes and caveats are thunks resolved at render in the reader's language
 * (metricHelpRegistry's rule), so this table holds no language of its own.
 *
 * @module services/mediaApi/fieldMapping
 */
import type { TFunction } from 'i18next';

/** The eight metrics PlatformMetric carries, in the order the UI shows them. */
export const METRIC_KEYS = [
  'impressions',
  'clicks',
  'spend',
  'conversions',
  'conversion_value',
  'reach',
  'engagements',
  'video_views',
] as const;

export type MetricKey = (typeof METRIC_KEYS)[number];

export interface MetricSource {
  /**
   * API field names exactly as they appear in the client, so the drift test can
   * look for them. Empty means the client hardcodes 0 — the platform does not
   * give the number at all.
   */
  fields: string[];
  /** User-facing. Present whenever the value is not a plain 1:1 copy. */
  note?: (t: TFunction) => string;
}

export interface PlatformFieldMapping {
  platform: string;
  label: string;
  /** Client the mapping was read from. The drift test greps this file. */
  sourceFile: string;
  metrics: Record<MetricKey, MetricSource>;
  /** What a reader would otherwise get wrong about this platform. */
  caveats: Array<(t: TFunction) => string>;
}

/** TikTok and LINE both lack it, in the same words. */
const NO_ENGAGEMENTS = (t: TFunction): string => t('help.fieldMap.noEngagements');

export const TIKTOK_CONVERSIONS = [
  'complete_payment',
  'form',
  'registration',
  'subscribe',
  'sales_lead',
  'app_install',
  'download_start',
];

export const PLATFORM_FIELD_MAPPINGS: PlatformFieldMapping[] = [
  {
    platform: 'google',
    label: 'Google Ads',
    sourceFile: 'src/services/mediaApi/GoogleAdsClient.ts',
    metrics: {
      impressions: { fields: ['impressions'] },
      clicks: { fields: ['clicks'] },
      spend: { fields: ['costMicros'], note: (t) => t('help.fieldMap.google.spend') },
      conversions: { fields: ['conversions'], note: (t) => t('help.fieldMap.google.conversions') },
      conversion_value: { fields: ['conversionsValue'] },
      reach: { fields: [], note: (t) => t('help.fieldMap.google.reach') },
      engagements: { fields: ['engagements', 'interactions'], note: (t) => t('help.fieldMap.google.engagements') },
      video_views: { fields: [], note: (t) => t('help.fieldMap.google.videoViews') },
    },
    caveats: [(t) => t('help.fieldMap.google.noReachVideo'), (t) => t('help.fieldMap.google.pageView')],
  },
  {
    platform: 'facebook',
    label: 'Facebook',
    sourceFile: 'src/services/mediaApi/FacebookAdsClient.ts',
    metrics: {
      impressions: { fields: ['impressions'] },
      clicks: { fields: ['clicks'] },
      spend: { fields: ['spend'] },
      conversions: { fields: ['actions'], note: (t) => t('help.fieldMap.facebook.conversions') },
      conversion_value: { fields: ['action_values'], note: (t) => t('help.fieldMap.facebook.conversionValue') },
      reach: { fields: ['reach'] },
      engagements: {
        fields: ['engagement', 'post_engagement'],
        note: (t) => t('help.fieldMap.facebook.engagements'),
      },
      video_views: { fields: ['video_view'], note: (t) => t('help.fieldMap.facebook.videoViews') },
    },
    caveats: [
      (t) => t('help.fieldMap.facebook.allMetrics', { n: METRIC_KEYS.length }),
      (t) => t('help.fieldMap.facebook.sharedActions'),
    ],
  },
  {
    platform: 'tiktok',
    label: 'TikTok',
    sourceFile: 'src/services/mediaApi/TikTokAdsClient.ts',
    metrics: {
      impressions: { fields: ['impressions'] },
      clicks: { fields: ['clicks'] },
      spend: { fields: ['spend'] },
      conversions: {
        fields: TIKTOK_CONVERSIONS,
        note: (t) => t('help.fieldMap.tiktok.conversions', { n: TIKTOK_CONVERSIONS.length }),
      },
      conversion_value: {
        fields: ['total_complete_payment_rate'],
        note: (t) => t('help.fieldMap.tiktok.conversionValue'),
      },
      reach: { fields: ['reach'] },
      engagements: { fields: [], note: (t) => t('help.fieldMap.tiktok.engagements') },
      video_views: { fields: ['video_play_actions'] },
    },
    caveats: [NO_ENGAGEMENTS, (t) => t('help.fieldMap.tiktok.dateChunks')],
  },
  {
    platform: 'line',
    label: 'LINE Ads',
    sourceFile: 'src/services/mediaApi/LINEAdsClient.ts',
    metrics: {
      impressions: { fields: ['imp'] },
      clicks: { fields: ['click'] },
      spend: { fields: ['cost'], note: (t) => t('help.fieldMap.line.spend') },
      conversions: { fields: ['cv'] },
      conversion_value: { fields: ['cvPurchaseValue'] },
      reach: { fields: ['reach'] },
      engagements: { fields: [], note: (t) => t('help.fieldMap.line.engagements') },
      video_views: { fields: ['videoCompletions'], note: (t) => t('help.fieldMap.line.videoViews') },
    },
    caveats: [NO_ENGAGEMENTS, (t) => t('help.fieldMap.line.completions'), (t) => t('help.fieldMap.line.jws')],
  },
  {
    platform: 'analytics',
    label: 'Google Analytics',
    sourceFile: 'src/services/mediaApi/GoogleAnalyticsClient.ts',
    metrics: {
      impressions: { fields: [], note: (t) => t('help.fieldMap.analytics.impressions') },
      clicks: { fields: ['eventCount'], note: (t) => t('help.fieldMap.analytics.clicks') },
      spend: { fields: [], note: (t) => t('help.fieldMap.analytics.spend') },
      conversions: { fields: ['conversions'] },
      conversion_value: { fields: ['totalRevenue'] },
      reach: { fields: ['activeUsers'], note: (t) => t('help.fieldMap.analytics.reach') },
      engagements: { fields: ['eventCount'], note: (t) => t('help.fieldMap.analytics.engagements') },
      video_views: { fields: [], note: (t) => t('help.fieldMap.analytics.videoViews') },
    },
    caveats: [
      (t) => t('help.fieldMap.analytics.notAd'),
      (t) => t('help.fieldMap.analytics.sameValue'),
      (t) => t('help.fieldMap.analytics.allTraffic'),
    ],
  },
];

/** True when the platform does not supply the metric at all (client stores 0). */
export const isAbsent = (m: PlatformFieldMapping, key: MetricKey): boolean =>
  m.metrics[key].fields.length === 0;
